import { Button } from "antd";
import { ColumnProps } from "antd/lib/table";
import React, { useContext, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { DndContext, DragEndEvent, closestCenter } from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { BarsOutlined } from "@ant-design/icons";
import Table from "../../general/table-components/table";
import { tableOnChange } from "../../../utils/helpers/table-sorts-filters";
import TrademarkContext from "../../../context/trademark/context";
import { ITrademark } from "../../../models/trademark/response";
import { ISort, IStaticFilter } from "../../../models/base/base-query";
import EndPoints from "../../../services/end-points";
import { useParams } from "react-router-dom";interface RowContextProps {
  setActivatorNodeRef?: (element: HTMLElement | null) => void;
  listeners?: any;
}
import { useLocation } from "react-router-dom";
const RowContext = React.createContext<RowContextProps>({});

const DragHandle = ({ isSortingEnabled }: { isSortingEnabled: boolean }) => {
  const { setActivatorNodeRef, listeners } = useContext(RowContext);

  return (
    <Button
      type="text"
      size="small"
      icon={<BarsOutlined />}
      style={{
        cursor: isSortingEnabled ? "grab" : "not-allowed",
        opacity: isSortingEnabled ? 1 : 0.5,
      }}
      ref={setActivatorNodeRef}
      {...(isSortingEnabled ? listeners : {})}
      disabled={!isSortingEnabled}
    />
  );
};

const Row: React.FC<{ "data-row-key": string } & React.HTMLAttributes<HTMLTableRowElement>> = (props) => {
  const { attributes, listeners, setNodeRef, setActivatorNodeRef, transform, transition, isDragging } = useSortable({
    id: props["data-row-key"],
  });

  const style: React.CSSProperties = {
    ...props.style,
    transform: CSS.Translate.toString(transform),
    transition,
    ...(isDragging ? { position: "relative", zIndex: 9999 } : {}),
  };

  const contextValue = useMemo(() => ({ setActivatorNodeRef, listeners }), [setActivatorNodeRef, listeners]);

  return (
    <RowContext.Provider value={contextValue}>
      <tr {...props} ref={setNodeRef} style={style} {...attributes} />
    </RowContext.Provider>
  );
};

interface IProps {
  isSortingEnabled: boolean;
  sectionId?: string;
  sortFilter?: string;
  freeDelivery?: boolean;
  discountOffer?: boolean;
  refreshTrigger?: boolean;
  onLoadingChange?: (loading: boolean) => void;
}

const SectionCompanyTable: React.FC<IProps> = ({ isSortingEnabled, freeDelivery, discountOffer, refreshTrigger, onLoadingChange }) => {
  const { t } = useTranslation();
  const { id, sortFilter } = useParams();// Inside your component:
  const location = useLocation();
  const sectionId = location.state?.sectionId;
  //console.log('location',location.state)
  const { list, actions, loading, query } = useContext(TrademarkContext);
  const [dataSource, setDataSource] = useState<ITrademark[]>([]);
  const [trademarLoading, setTrademarkLoading] = useState<boolean>(true);
  const [sorts, setSorts] = useState<ISort[]>([]);
  const [staticFilters, setStaticFilters] = useState<IStaticFilter[]>(query?.filters || []);
  const [changeData, setChangeData] = useState<boolean>(false);

  // Notify parent about loading state changes
  useEffect(() => {
    if (onLoadingChange) {
      onLoadingChange(trademarLoading);
    }
  }, [trademarLoading, onLoadingChange]);

  useEffect(() => {
    const getTradmark = async () => {
      try {
        setTrademarkLoading(true);
        // Determine the correct filter value based on sortFilter type
        let filterValue = sectionId;

        // For boolean-type filters, use value 1 instead of section ID
        if (sortFilter === "hasFreeDeliveryProducts" || sortFilter === "hasDiscountProducts") {
          filterValue = 1;
        }

        let filters = [
            {
              name: sortFilter,
              value: filterValue,
              operation: "eq",
            }
          ];

        const {data} = await EndPoints.trademark.getAllTrademark({
          filters,
          perPage: -1
        })
        setDataSource(data.data);
        setTrademarkLoading(false)
      } catch (error) {
      } finally {
        setTrademarkLoading(false)
      }
    };
    getTradmark();
  }, [changeData, sortFilter, sectionId, refreshTrigger]);

  // Update query when sorts change
  useEffect(() => {
    if (sorts.length > 0) {
      actions.setQuery({
        ...query,
        orders: sorts,
      });
    }
  }, [sorts]);

  const onDragEnd = async ({ active, over }: DragEndEvent) => {
    if (!isSortingEnabled || !over || active.id === over.id) return;

    try {
      // Update local data immediately for smooth UI experience
      const oldIndex = dataSource.findIndex(item => item.id === active.id);
      const newIndex = dataSource.findIndex(item => item.id === over.id);
      const newData = arrayMove(dataSource, oldIndex, newIndex);

      // Update orderInCategory based on new position
      const updatedData = newData.map((item, index) => ({
        ...item,
        orderInCategory: index + 1
      }));

      setDataSource(updatedData);

      // Send update to server
      await actions.updateCompaniesArrangementSectionList({
        sectionId: sectionId?Number(sectionId):1,
        trademarkId: Number(active.id),
        orderInCategory: newIndex + 1
      });

      // Refresh data to ensure consistency
      setChangeData(!changeData)
    } catch (error) {
      console.error("Error updating order:", error);
      // Revert to original data if error occurs
      if (list?.data) setDataSource([...list.data]);
    }
  };

  const columns: ColumnProps<ITrademark>[] = [
    {
      key: "sort",
      align: "center",
      width: 80,
      render: () => <DragHandle isSortingEnabled={isSortingEnabled} />,
    },
    {
      title: t("id"),
      dataIndex: "id",
      key: "id",
      sorter: true,
      width: 100,
      align: "center",
    },
    {
      title: t("name"),
      dataIndex: "name",
      align: "center",
      key: "name",
      sorter: true,
    },
    // Order column with conditional rendering based on filter type
    {
      title: t("order"),
      align: "center",
      key: "order",
      sorter: true,
      render: (_: any, record: ITrademark) => {
        // If filtering by freeDelivery or discountOffer, use orderInSection
        if (sortFilter === "hasFreeDeliveryProducts" || sortFilter === "hasDiscountProducts") {
          return record.orderInSection ?? '-';
        }
        // If filtering by category, use orderInCategory
        else if (sortFilter === "hasProductsInThisCategory") {
          return record.orderInCategory ?? '-';
        }
        // Otherwise use order
        return record.order ?? '-';
      },
    },
  ];

  return (
    <DndContext collisionDetection={closestCenter} onDragEnd={onDragEnd}>
      <SortableContext
        items={dataSource.map(item => item.id)}
        strategy={verticalListSortingStrategy}
      >
        <Table<ITrademark>
          rowKey="id"
          showPagination={false}
          columns={columns}
          dataSource={dataSource}
          loading={trademarLoading}
          total={list?.total}
          size="small"
          scroll={{ x: 1400 }}
          onChange={(pagination, filters, sorter) => {
            tableOnChange(
              filters,
              sorter,
              {}, // tableFiltersProps if needed
              setSorts,
              setStaticFilters
            );
          }}
          components={{
            body: {
              row: Row,
            },
          }}
        />
      </SortableContext>
    </DndContext>
  );
};

export default SectionCompanyTable;